import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { SiteNav } from '../components/SiteNav'
import { setPageMeta } from '../utils'

const FAQ_SECTIONS = [
  {
    heading: 'Privacy & Security',
    questions: [
      {
        q: 'Does arisk.ai store or read my text?',
        a: 'No. arisk.ai processes everything locally in your browser using JavaScript. Your text never leaves your device: it is not sent to any server, stored in any database, or seen by anyone. You can verify this yourself: open your browser\'s DevTools, go to the Network tab, and use the tool. You will see no outbound requests containing your data.',
      },
      {
        q: 'Is it safe to use with confidential or legally privileged documents?',
        a: 'Yes. Because all processing happens in your browser, there is no transmission of your data to a third party. The redaction map, which links tokens to original words, exists only in your browser\'s memory for the duration of your session. Once you close the tab, it is gone. There is nothing to intercept, subpoena, or breach.',
      },
      {
        q: 'What happens if I close the browser or tab mid-session?',
        a: 'The redaction map is held only in your browser\'s memory and is not persisted anywhere. If you close the tab or browser before restoring your text, the mapping is lost and you will not be able to automatically restore the original words. If you are working on a long session, keep the tab open until you have finished — or note down the token mappings from the Key panel before closing.',
      },
      {
        q: 'Does it comply with GDPR, HIPAA, or other data regulations?',
        a: 'Since arisk.ai never processes, stores, or transmits your data, it sidesteps most compliance concerns entirely. There is no data controller in the traditional sense because there is no data leaving your device. That said, arisk.ai is a tool to help you manage what you share with AI; it is not a substitute for your organisation\'s data governance policies.',
      },
      {
        q: 'Does arisk.ai use cookies or tracking?',
        a: 'We use browser localStorage to save your theme preference and instruction text between sessions. This data stays on your device and is never transmitted. We do not use third-party advertising trackers.',
      },
    ],
  },
  {
    heading: 'How It Works',
    questions: [
      {
        q: 'How does the redaction work?',
        a: 'Click or tap any word and arisk.ai replaces it with a unique placeholder token.',
        example: true,
        suffix: 'The mapping between tokens and original words is stored in your browser memory. When you paste the AI\'s response back into the Restore tab and click Restore, the tokens are swapped back for the originals automatically.',
      },
      {
        q: 'What is the "Context for AI" field?',
        a: 'It is an optional instruction block that is copied above your redacted text. Use it to tell the AI what task you need help with and to flag that the text contains placeholder tokens it should preserve exactly. arisk.ai includes presets for common use cases such as legal review, medical notes, and code review — or you can write your own.',
      },
      {
        q: 'Will the AI still understand my text after redaction?',
        a: 'Yes, in most cases. AI language models handle placeholder text well. The structure, grammar, and context of your document remain intact; only the sensitive values are replaced. For best results, use the "Context for AI" field to add a brief note explaining that the text contains placeholder tokens the AI should treat as opaque identifiers.',
      },
      {
        q: 'What types of data can I redact?',
        a: 'Any individual word or term: names, email addresses, company names, account numbers, contract values, dates, identifiers, or any other sensitive item. Click or tap a word to redact it individually. To redact a multi-word phrase, select the full phrase and right-click.',
      },
      {
        q: 'Can I use it with PDFs?',
        a: 'Yes. Click "Upload PDF" to extract text from a PDF file. Extraction happens locally in your browser using PDF.js; the file is never uploaded anywhere. You can then redact words in the extracted text and copy it safely to any AI tool.',
      },
      {
        q: 'What is a "token" in arisk.ai?',
        a: 'A token is the placeholder that replaces a sensitive word. Tokens follow the format xxxxx followed by a four-digit number (e.g., xxxxx0001, xxxxx0002). They are distinctive enough that an AI will preserve them verbatim in its response, making restoration reliable.',
      },
    ],
  },
  {
    heading: 'General',
    questions: [
      {
        q: 'Is arisk.ai free?',
        a: 'Yes, completely free with no account required. There is nothing to sign up for.',
      },
      {
        q: 'Which AI tools does it work with?',
        a: 'Any. arisk.ai is AI-agnostic: it works with ChatGPT, Claude, Gemini, Copilot, Grok, or any other AI tool or LLM. You copy the redacted text, paste it into whichever AI you use, then paste the response back to restore the originals.',
      },
      {
        q: 'Does it work on mobile?',
        a: 'Yes. On touch devices, tap any word to redact it instantly, and tap a token to restore the original. No text selection is required. The tool is fully functional on mobile browsers, though for heavy document work a desktop browser is more convenient.',
      },
      {
        q: 'Do I need to install anything?',
        a: 'No. arisk.ai runs entirely in your browser. There is no app to download, no extension to install, and no account to create.',
      },
    ],
  },
]

const JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_SECTIONS.flatMap((section) =>
    section.questions.map(({ q, a, suffix }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: suffix ? `${a} ${suffix}` : a,
      },
    }))
  ),
}

function TokenExample() {
  return (
    <div className="my-3 flex flex-wrap items-center gap-3 text-sm font-mono">
      <span className="inline-flex gap-1 items-center bg-gray-100 dark:bg-neutral-700/60 rounded px-3 py-1.5 text-gray-800 dark:text-neutral-200">
        John Smith
      </span>
      <span className="text-gray-400 dark:text-neutral-500 text-base select-none">→</span>
      <span className="inline-flex gap-1 items-center bg-gray-100 dark:bg-neutral-700/60 rounded px-3 py-1.5">
        <span className="bg-blue-200 dark:bg-blue-500/30 text-blue-900 dark:text-blue-200 rounded-sm px-0.5">xxxxx0001</span>
        <span className="mx-0.5" />
        <span className="bg-blue-200 dark:bg-blue-500/30 text-blue-900 dark:text-blue-200 rounded-sm px-0.5">xxxxx0002</span>
      </span>
    </div>
  )
}

export function FaqPage() {
  const [openSet, setOpenSet] = useState(new Set())

  useEffect(() => {
    setPageMeta({
      title: 'FAQ | arisk.ai',
      description: 'Frequently asked questions about arisk.ai: privacy, security, how redaction works, and more.',
      canonical: 'https://arisk.ai/faq',
    })
  }, [])

  function toggle(q) {
    setOpenSet((prev) => {
      const next = new Set(prev)
      if (next.has(q)) {
        next.delete(q)
      } else {
        next.add(q)
      }
      return next
    })
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-900 text-gray-800 dark:text-neutral-200">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(JSON_LD) }}
      />

      <SiteNav />

      <div className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Frequently Asked Questions
        </h1>
        <p className="text-gray-500 dark:text-neutral-400 mb-12">
          Everything you need to know about arisk.ai: how it works, what it protects, and why it is safe.
        </p>

        <div className="space-y-10">
          {FAQ_SECTIONS.map((section) => (
            <section key={section.heading}>
              <h2 className="text-xs font-semibold text-gray-400 dark:text-neutral-500 uppercase tracking-widest mb-4">
                {section.heading}
              </h2>
              <div className="divide-y divide-gray-100 dark:divide-neutral-800">
                {section.questions.map(({ q, a, example, suffix }) => {
                  const isOpen = openSet.has(q)
                  return (
                    <div key={q}>
                      <button
                        onClick={() => toggle(q)}
                        className="w-full flex items-center justify-between gap-4 text-left py-4 transition-colors group"
                        aria-expanded={isOpen}
                      >
                        <span className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{q}</span>
                        <svg
                          className={`w-4 h-4 text-gray-400 dark:text-neutral-500 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {isOpen && (
                        <div className="pb-4">
                          <p className="text-sm text-gray-600 dark:text-neutral-400 leading-relaxed">{a}</p>
                          {example && <TokenExample />}
                          {suffix && (
                            <p className="text-sm text-gray-600 dark:text-neutral-400 leading-relaxed mt-2">{suffix}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </section>
          ))}
        </div>

        <p className="mt-12 text-sm text-gray-500 dark:text-neutral-400">
          Want to know more about the team?{' '}
          <Link to="/about" className="text-blue-600 dark:text-blue-400 hover:underline">
            Visit the About page.
          </Link>
        </p>
      </div>
    </div>
  )
}
